<template>
  <v-dialog
    :value="openModal"
    max-width="900px"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="primary white--text">
        <v-icon left color="white">mdi-receipt</v-icon>
        Ordem de Faturamento
      </v-card-title>

      <v-card-text class="pa-4" v-if="loading">
        <div class="text-center py-8">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
            width="6"
          ></v-progress-circular>
          <div class="mt-4 text-body-1">Carregando dados da ordem de faturamento...</div>
        </div>
      </v-card-text>

      <v-card-text class="pa-4" v-else-if="billingOrder">
        <!-- Informações Gerais -->
        <h3 class="text-h6 mb-3 primary--text">
          <v-icon left color="primary">mdi-information</v-icon>
          Informações Gerais
        </h3>
        <v-row>
          <v-col cols="12" md="6">
            <div class="mb-3">
              <div class="text-caption grey--text">ID da Ordem</div>
              <div class="text-body-1 font-weight-medium">{{ billingOrder.id_doc }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="mb-3">
              <div class="text-caption grey--text">Status</div>
              <v-chip
                :color="getStatusColor(billingOrder.status)"
                text-color="white"
                small
              >
                {{ getStatusText(billingOrder.status) }}
              </v-chip>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="mb-3">
              <div class="text-caption grey--text">Data de Criação</div>
              <div class="text-body-1">{{ formatDate(billingOrder.createdAt) }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="6" v-if="billingOrder.billingType === 'single'">
            <div class="mb-3">
              <div class="text-caption grey--text">Vencimento</div>
              <div class="text-body-1">{{ formatDate(billingOrder.dueDate) }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="6" v-if="billingOrder.billingType === 'installments'">
            <div class="mb-3">
              <div class="text-caption grey--text">Tipo de Faturamento</div>
              <v-chip color="info" text-color="white" small>
                Parcelado
              </v-chip>
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Forma de Pagamento -->
        <h3 class="text-h6 mb-3 primary--text">
          <v-icon left color="primary">mdi-credit-card</v-icon>
          Forma de Pagamento
        </h3>
        <div class="mb-4">
          <v-chip
            v-for="method in billingOrder.paymentMethod"
            :key="method"
            color="primary"
            text-color="white"
            small
            class="mr-2 mb-2"
          >
            {{ getPaymentMethodText(method) }}
          </v-chip>
        </div>

        <v-divider class="my-4"></v-divider>

        <!-- Valores -->
        <h3 class="text-h6 mb-3 primary--text">
          <v-icon left color="primary">mdi-currency-usd</v-icon>
          Valores
        </h3>
        <v-row>
          <v-col cols="12" md="6">
            <div class="mb-3">
              <div class="text-caption grey--text">Valor Total</div>
              <div class="text-h6 font-weight-bold success--text">
                {{ formatCurrency(billingOrder.total || billingOrder.totalValue) }}
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Parcelas (se for parcelado) -->
        <template v-if="billingOrder.billingType === 'installments' && billingOrder.installments && billingOrder.installments.length > 0">
          <v-divider class="my-4"></v-divider>
          <h3 class="text-h6 mb-3 primary--text">
            <v-icon left color="primary">mdi-cash-multiple</v-icon>
            Parcelas
          </h3>
          
          <v-card
            v-for="(installment, index) in billingOrder.installments"
            :key="index"
            outlined
            class="mb-3"
          >
            <v-card-text>
              <v-row align="center">
                <v-col cols="12" md="3">
                  <div class="text-caption grey--text">
                    {{ installment.type === 'entry' ? 'Entrada' : `Parcela ${installment.number || index}` }}
                  </div>
                  <div class="text-h6 font-weight-bold" :class="installment.type === 'entry' ? 'success--text' : 'primary--text'">
                    {{ formatCurrency(installment.value) }}
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="text-caption grey--text">Vencimento</div>
                  <div class="text-body-1 font-weight-medium">
                    {{ formatDate(installment.dueDate) }}
                  </div>
                </v-col>
                <v-col cols="12" md="3">
                  <v-chip
                    :color="installment.type === 'entry' ? 'success' : 'primary'"
                    text-color="white"
                    small
                  >
                    {{ installment.type === 'entry' ? 'Entrada' : 'Parcela' }}
                  </v-chip>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Resumo de Parcelamento -->
          <v-card outlined class="mt-4">
            <v-card-title class="subtitle-1">
              Resumo do Parcelamento
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="4">
                  <div class="text-caption grey--text">Valor Total</div>
                  <div class="text-h6 font-weight-bold">{{ formatCurrency(billingOrder.total || billingOrder.totalValue) }}</div>
                </v-col>
                <v-col cols="12" md="4" v-if="billingOrder.entryType === 'percentage'">
                  <div class="text-caption grey--text">Entrada</div>
                  <div class="text-h6 font-weight-bold success--text">
                    {{ billingOrder.entryPercentage }}% ({{ formatCurrency(getEntryValue()) }})
                  </div>
                </v-col>
                <v-col cols="12" md="4" v-else-if="billingOrder.entryType === 'value'">
                  <div class="text-caption grey--text">Entrada</div>
                  <div class="text-h6 font-weight-bold success--text">
                    {{ formatCurrency(billingOrder.entryValue) }}
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-caption grey--text">Número de Parcelas</div>
                  <div class="text-h6 font-weight-bold">{{ billingOrder.numberOfInstallments || billingOrder.installments.length - 1 }}</div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </template>

        <v-divider class="my-4"></v-divider>

        <!-- Dados do Usuário -->
        <h3 class="text-h6 mb-3 primary--text">
          <v-icon left color="primary">mdi-account</v-icon>
          Dados do Usuário
        </h3>
        <v-row v-if="billingOrder.user">
          <v-col cols="12" md="6">
            <div class="mb-3">
              <div class="text-caption grey--text">Nome</div>
              <div class="text-body-1">{{ billingOrder.user.name || '-' }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="mb-3">
              <div class="text-caption grey--text">Email</div>
              <div class="text-body-1">{{ billingOrder.user.email || '-' }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="6" v-if="billingOrder.user.cpf">
            <div class="mb-3">
              <div class="text-caption grey--text">CPF</div>
              <div class="text-body-1">{{ billingOrder.user.cpf }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="6" v-if="billingOrder.user.cnpj">
            <div class="mb-3">
              <div class="text-caption grey--text">CNPJ</div>
              <div class="text-body-1">{{ billingOrder.user.cnpj }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="6" v-if="billingOrder.user.userType">
            <div class="mb-3">
              <div class="text-caption grey--text">Tipo</div>
              <div class="text-body-1">
                {{ billingOrder.user.userType === 'physical' ? 'Pessoa Física' : 'Pessoa Jurídica' }}
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="6" v-if="billingOrder.user.nameResponsible">
            <div class="mb-3">
              <div class="text-caption grey--text">Nome do Responsável</div>
              <div class="text-body-1">{{ billingOrder.user.nameResponsible }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="6" v-if="billingOrder.user.cpfResponsible">
            <div class="mb-3">
              <div class="text-caption grey--text">CPF do Responsável</div>
              <div class="text-body-1">{{ billingOrder.user.cpfResponsible }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="6" v-if="billingOrder.user.emailResponsible">
            <div class="mb-3">
              <div class="text-caption grey--text">Email do Responsável</div>
              <div class="text-body-1">{{ billingOrder.user.emailResponsible }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="6" v-if="billingOrder.user.phoneResponsible">
            <div class="mb-3">
              <div class="text-caption grey--text">Telefone do Responsável</div>
              <div class="text-body-1">{{ billingOrder.user.phoneResponsible }}</div>
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-4" v-if="billingOrder.user"></v-divider>

        <!-- Dados de Endereço -->
        <h3 class="text-h6 mb-3 primary--text">
          <v-icon left color="primary">mdi-map-marker</v-icon>
          Dados de Endereço
        </h3>
        <v-row v-if="billingOrder.address">
          <v-col cols="12" md="8">
            <div class="mb-3">
              <div class="text-caption grey--text">Logradouro</div>
              <div class="text-body-1">{{ billingOrder.address.street || '-' }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="4" v-if="billingOrder.address.apartmentNumber">
            <div class="mb-3">
              <div class="text-caption grey--text">Número do Apartamento</div>
              <div class="text-body-1">{{ billingOrder.address.apartmentNumber }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="4" v-if="billingOrder.address.residentialNumber">
            <div class="mb-3">
              <div class="text-caption grey--text">Número Residencial</div>
              <div class="text-body-1">{{ billingOrder.address.residentialNumber }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="4" v-if="billingOrder.address.complement">
            <div class="mb-3">
              <div class="text-caption grey--text">Complemento</div>
              <div class="text-body-1">{{ billingOrder.address.complement }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="mb-3">
              <div class="text-caption grey--text">Bairro</div>
              <div class="text-body-1">{{ billingOrder.address.district || '-' }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="mb-3">
              <div class="text-caption grey--text">Cidade</div>
              <div class="text-body-1">{{ billingOrder.address.city || '-' }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="mb-3">
              <div class="text-caption grey--text">Estado</div>
              <div class="text-body-1">{{ billingOrder.address.state || '-' }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="mb-3">
              <div class="text-caption grey--text">CEP</div>
              <div class="text-body-1">{{ billingOrder.address.zipCode || '-' }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="4" v-if="billingOrder.address.country">
            <div class="mb-3">
              <div class="text-caption grey--text">País</div>
              <div class="text-body-1">{{ billingOrder.address.country }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="4" v-if="billingOrder.address.ibgeCityCode">
            <div class="mb-3">
              <div class="text-caption grey--text">Código IBGE</div>
              <div class="text-body-1">{{ billingOrder.address.ibgeCityCode }}</div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text class="pa-4" v-else-if="!loading && !billingOrder">
        <v-alert type="error" outlined>
          <div class="font-weight-bold mb-2">Ordem de faturamento não encontrada.</div>
          <div class="text-caption">
            ID informado: <strong>{{ billingOrderId || 'Não informado' }}</strong>
          </div>
          <div class="text-caption mt-1">
            Verifique se o ID está correto e se a ordem de faturamento existe no sistema.
          </div>
        </v-alert>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          v-if="billingOrder && billingOrder.status === 'pending' && !isFranchisee"
          color="success"
          @click="markAsPaid"
          :loading="updatingStatus"
          class="mr-2"
        >
          <v-icon left>mdi-check-circle</v-icon>
          Marcar como Paga
        </v-btn>
        <v-btn
          color="primary"
          @click="close"
        >
          Fechar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import BillingOrderService from '@/services/firebase/BillingOrder/BillingOrderService';
import { showSuccessSnackBar, showErrorSnackBar } from "@/utils/SnackBartHelper";
import { showConfirmDialogAlert } from "@/utils/DialogAlertHelper";

export default {
  name: 'BillingOrderViewDialog',
  props: {
    openModal: {
      type: Boolean,
      default: false
    },
    billingOrderId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      billingOrder: null,
      updatingStatus: false
    };
  },
  computed: {
    ...mapGetters([
      "getCurrentUserRank",
    ]),
    isFranchisee() {
      return this.getCurrentUserRank === "Franqueado";
    },
  },
  watch: {
    openModal: {
      handler(newValue) {
        console.log('watch openModal:', newValue, 'billingOrderId:', this.billingOrderId);
        if (newValue && this.billingOrderId) {
          this.loadBillingOrder();
        } else {
          this.billingOrder = null;
        }
      },
      immediate: true
    },
    billingOrderId: {
      handler(newValue) {
        console.log('watch billingOrderId:', newValue, 'openModal:', this.openModal);
        if (newValue && this.openModal) {
          this.loadBillingOrder();
        }
      },
      immediate: true
    }
  },
  mounted() {
    // Garantir que carregue se o modal já estiver aberto e o ID já estiver definido
    console.log('mounted - openModal:', this.openModal, 'billingOrderId:', this.billingOrderId);
    if (this.openModal && this.billingOrderId) {
      this.loadBillingOrder();
    }
  },
  methods: {
    async loadBillingOrder() {
      if (!this.billingOrderId) {
        this.billingOrder = null;
        return;
      }

      this.loading = true;
      this.billingOrder = null;
      try {
        console.log('Carregando ordem de faturamento com ID:', this.billingOrderId);
        const order = await BillingOrderService.getBillingOrderById(this.billingOrderId);
        console.log('Ordem de faturamento carregada:', order);
        this.billingOrder = order;
      } catch (error) {
        console.error('Erro ao carregar ordem de faturamento:', error);
        console.error('ID utilizado:', this.billingOrderId);
        this.billingOrder = null;
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      if (!date) return 'Não informado';
      if (typeof date === 'string') {
        const d = new Date(date);
        if (isNaN(d.getTime())) return date;
        return d.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      if (date.toDate) {
        return date.toDate().toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    formatCurrency(value) {
      if (!value) return 'R$ 0,00';
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(numValue);
    },
    getStatusColor(status) {
      const colors = {
        'pending': 'orange',
        'processing': 'blue',
        'completed': 'success',
        'paid': 'green',
        'cancelled': 'error'
      };
      return colors[status] || 'grey';
    },
    getStatusText(status) {
      const texts = {
        'pending': 'Pendente',
        'processing': 'Em Processamento',
        'completed': 'Concluída',
        'paid': 'Paga',
        'cancelled': 'Cancelada'
      };
      return texts[status] || status;
    },
    getPaymentMethodText(method) {
      const texts = {
        'bank_slip': 'Boleto',
        'credit_card': 'Cartão de Crédito',
        'pix': 'PIX',
        'all': 'Todas'
      };
      return texts[method] || method;
    },
    getEntryValue() {
      if (!this.billingOrder || this.billingOrder.billingType !== 'installments') return 0;
      
      const total = parseFloat(this.billingOrder.total || this.billingOrder.totalValue || 0);
      
      if (this.billingOrder.entryType === 'percentage') {
        const percentage = parseFloat(this.billingOrder.entryPercentage || 0);
        return (total * percentage) / 100;
      } else if (this.billingOrder.entryType === 'value') {
        return parseFloat(this.billingOrder.entryValue || 0);
      }
      
      // Se houver entrada nas parcelas, pegar o valor da primeira parcela
      if (this.billingOrder.installments && this.billingOrder.installments.length > 0) {
        const entry = this.billingOrder.installments.find(inst => inst.type === 'entry');
        if (entry) {
          return parseFloat(entry.value || 0);
        }
      }
      
      return 0;
    },
    close() {
      this.$emit('close');
    },
    markAsPaid() {
      if (!this.billingOrder || !this.billingOrder.id_doc) {
        showErrorSnackBar('Erro: Ordem de faturamento não encontrada.');
        return;
      }

      if (this.billingOrder.status !== 'pending') {
        showErrorSnackBar('Apenas ordens com status "Pendente" podem ser marcadas como pagas.');
        return;
      }

      // Confirmar ação usando DialogAlert
      showConfirmDialogAlert(
        `Deseja realmente marcar a ordem de faturamento ${this.billingOrder.id_doc} como paga?`,
        async () => {
          try {
            this.updatingStatus = true;
            
            // Atualizar status para 'paid'
            await BillingOrderService.editBillingOrder(this.billingOrder.id_doc, {
              status: 'paid',
              paidAt: new Date()
            });
            
            showSuccessSnackBar('Ordem de faturamento marcada como paga com sucesso!');
            
            // Recarregar dados da ordem
            await this.loadBillingOrder();
          } catch (error) {
            console.error('Erro ao marcar ordem como paga:', error);
            showErrorSnackBar('Erro ao marcar ordem como paga. Tente novamente.');
          } finally {
            this.updatingStatus = false;
          }
        },
        () => {
          // Função de cancelamento (não precisa fazer nada)
        }
      );
    }
  }
};
</script>

<style scoped>
.v-card-title {
  display: flex;
  align-items: center;
}
</style>

